import { useCallback, useEffect, useState } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { teamName, packageName } from '../../common/constants.tsx';
import type { ProformaProjectFormState, ProformaReqTypeForms } from './types.tsx';

export const useFetchProformaProjectForms = () => {
	const { data } = useProjectContext();
	const projectId = data?.projectId ?? undefined;
	const cloudId = useCloudId();

	return useCallback<() => Promise<ProformaReqTypeForms[]>>(
		() =>
			performGetRequest(
				`/gateway/api/proforma/cloudid/${cloudId}/api/1/projects/${projectId}/forms`,
			),
		[cloudId, projectId],
	);
};

export const useProformaProjectFormsOnMount = (): ProformaProjectFormState => {
	const [forms, setForms] = useState<ProformaReqTypeForms[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);

	const { data } = useProjectContext();
	const projectId = data?.projectId ?? undefined;

	const fetchProformaProjectForms = useFetchProformaProjectForms();

	const fetchForms = useCallback(async () => {
		setError(null);
		setIsLoading(true);

		try {
			const projectForms = await fetchProformaProjectForms();
			setForms(projectForms);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (exception: any) {
			fireErrorAnalytics({
				meta: {
					id: 'getProformaReqTypeProjectForms',
					packageName,
					teamName,
				},
				error: exception,
				attributes: {
					projectId,
				},
			});

			setError(exception);
		} finally {
			setIsLoading(false);
		}
	}, [projectId, fetchProformaProjectForms]);

	// Fetch forms once on mount
	useEffect(() => {
		fetchForms();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { forms, isLoading, error };
};
